import { useState } from 'react'
import {
  Box,
  Button,
  DialogContentText,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import {
  refusalDetail,
  usePolicyConfigurationDocumentAction,
  type PolicyDocument,
  type PolicyDocumentAction,
} from './BenefitPolicyService'
import { NumberedSectionCard } from '~/shared/ui'
import { PolicyDocumentDialog } from './PolicyDocumentDialog'
import { RecordDialog } from './RecordDialog'

interface DocumentsSectionProps {
  policyId: number
  documents: PolicyDocument[]
  isActive: boolean
  readOnly: boolean
}

/** Which dialog is up, and on which row. `add` carries no document. */
type Target = { kind: 'add' } | { kind: 'edit' | 'remove'; document: PolicyDocument }

const documentKind = ({ file_url }: PolicyDocument) => {
  if (!file_url) return 'External link'
  return /\.([a-z0-9]{2,4})(?:$|[?#])/i.exec(file_url)?.[1]?.toUpperCase() ?? 'File'
}

const AudienceVisibility = ({ audience, isVisible }: { audience: string; isVisible: boolean }) => {
  const label = isVisible ? `Visible to ${audience}` : `Hidden from ${audience}`
  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
      {isVisible ? (
        <VisibilityIcon fontSize="small" color="action" titleAccess={label} />
      ) : (
        <VisibilityOffIcon fontSize="small" color="disabled" titleAccess={label} />
      )}
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
    </Stack>
  )
}

const DocumentRow = ({
  document,
  readOnly,
  onEdit,
  onRemove,
}: {
  document: PolicyDocument
  readOnly: boolean
  onEdit: () => void
  onRemove: () => void
}) => (
  <Stack
    direction="row"
    spacing={1.5}
    sx={{
      alignItems: 'center',
      py: 1,
      borderBottom: 1,
      borderColor: 'divider',
      '&:last-of-type': { borderBottom: 0 },
    }}
    data-testid={`policy-document-${document.id}`}
  >
    <DescriptionOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', flex: 'none' }} />
    <Box sx={{ minWidth: 0, flex: 1 }}>
      {document.link ? (
        <Link
          href={document.link}
          target="_blank"
          rel="noopener"
          variant="body2"
          underline="hover"
          sx={{ fontWeight: 600, fontSize: '1.4rem' }}
        >
          {document.name}
        </Link>
      ) : (
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '1.4rem' }}>
          {document.name}
        </Typography>
      )}
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
        {documentKind(document)}
      </Typography>
    </Box>
    <Stack spacing={0.25} sx={{ flex: 'none' }}>
      <AudienceVisibility audience="members" isVisible={document.is_visible_to_member} />
      <AudienceVisibility audience="prospects" isVisible={document.is_visible_to_prospect} />
    </Stack>
    {!readOnly && (
      <Stack direction="row" spacing={0.5} sx={{ flex: 'none' }}>
        <Tooltip title="Edit">
          <IconButton
            size="small"
            onClick={onEdit}
            data-testid={`policy-document-${document.id}-edit`}
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Remove">
          <IconButton
            size="small"
            onClick={onRemove}
            data-testid={`policy-document-${document.id}-remove`}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    )}
  </Stack>
)

/**
 * Section 4: the plan documents attached to this configuration.
 *
 * Each action posts on its own and answers with the whole configuration, so the list repaints from
 * the server rather than from what was just typed. A clone carries no documents — a summary of
 * benefits states its own plan year inside the file — so a renewal's are attached here.
 */
export const DocumentsSection = ({
  policyId,
  documents,
  isActive,
  readOnly,
}: DocumentsSectionProps) => {
  const [target, setTarget] = useState<Target | null>(null)
  const documentAction = usePolicyConfigurationDocumentAction(policyId)

  const refusal = documentAction.isError
    ? refusalDetail(documentAction.error, 'Could not update the plan documents.')
    : undefined

  // Reset first: a refusal answers one attempt, and carrying it into the next dialog reads as a
  // fresh failure before anything has been sent.
  const openDialog = (next: Target) => {
    documentAction.reset()
    setTarget(next)
  }

  // Closes only on success: a refusal leaves the dialog open with the server's reason on it.
  const act = (action: PolicyDocumentAction) =>
    documentAction.mutate(action, { onSuccess: () => setTarget(null) })

  return (
    <NumberedSectionCard index={4} title="Plan documents" testId="section-documents">
      {documents.length === 0 ? (
        <Box
          sx={{
            border: 1,
            borderStyle: 'dashed',
            borderColor: 'divider',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
          }}
          data-testid="no-documents"
        >
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            No documents on this policy.
          </Typography>
        </Box>
      ) : (
        <Box>
          {documents.map(document => (
            <DocumentRow
              key={document.id}
              document={document}
              readOnly={readOnly}
              onEdit={() => openDialog({ kind: 'edit', document })}
              onRemove={() => openDialog({ kind: 'remove', document })}
            />
          ))}
        </Box>
      )}

      {!readOnly && (
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => openDialog({ kind: 'add' })}
          sx={{ mt: 1.5 }}
          data-testid="add-document"
        >
          Add document
        </Button>
      )}

      {(target?.kind === 'add' || target?.kind === 'edit') && (
        <PolicyDocumentDialog
          document={target.kind === 'edit' ? target.document : undefined}
          isActive={isActive}
          saving={documentAction.isPending}
          refusal={refusal}
          onClose={() => setTarget(null)}
          onConfirm={act}
        />
      )}
      {target?.kind === 'remove' && (
        <RecordDialog
          title={`Remove “${target.document.name}”?`}
          confirmLabel="Remove"
          destructive
          busy={documentAction.isPending}
          notice={
            isActive
              ? 'This policy is live. If this document is visible to members or prospects, they lose access as soon as you remove it.'
              : undefined
          }
          refusal={refusal}
          testId="remove-document"
          maxWidth="xs"
          onClose={() => setTarget(null)}
          onConfirm={() => act({ action: 'remove', document: target.document.id })}
        >
          <DialogContentText>
            It comes off this policy and stops reaching members and prospects.
          </DialogContentText>
        </RecordDialog>
      )}
    </NumberedSectionCard>
  )
}
